import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CreditCardIcon, CalendarIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const CreatePayment = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [shopOwners, setShopOwners] = useState([]);
  const [formData, setFormData] = useState({
    transaction_id: transactionId || '',
    amount: '',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'cash',
    reference_number: '',
    notes: ''
  });

  useEffect(() => {
    if (transactionId) {
      fetchTransaction(transactionId);
    } else {
      fetchTransactions();
    }
    fetchShopOwners();
  }, [transactionId]);

  const fetchTransaction = async (id) => {
    try {
      const response = await api.get(`/sales/transactions/${id}`);
      setTransaction(response.data);
    } catch (error) {
      toast.error('Failed to fetch transaction');
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await api.get('/sales/transactions', {
        params: { status: ['pending', 'partial', 'overdue'] }
      });
      setTransactions(response.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };

  const fetchShopOwners = async () => {
    try {
      const response = await api.get('/users/', {
        params: { user_type: 'shop_owner' }
      });
      setShopOwners(response.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch shop owners');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.transaction_id) {
      toast.error('Please select a transaction');
      return;
    }

    const selectedTransaction = transaction || 
      transactions.find(t => t.id === formData.transaction_id);

    if (formData.amount > selectedTransaction.balance) {
      toast.error('Payment amount cannot exceed balance');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/sales/transactions/${formData.transaction_id}/payments`, {
        amount: formData.amount,
        payment_date: formData.payment_date,
        payment_method: formData.payment_method,
        reference_number: formData.reference_number,
        notes: formData.notes
      });
      toast.success('Payment recorded successfully');
      navigate('/payments');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const selectedTransaction = transaction || 
    transactions.find(t => t.id === formData.transaction_id);

  const getShopOwnerName = (shopOwnerId) => {
    const owner = shopOwners.find(o => o.profile?.id === shopOwnerId);
    return owner?.profile?.business_name || 'Unknown';
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Record Payment
          </h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Transaction Selection */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Transaction</h3>
              <p className="mt-1 text-sm text-gray-500">
                Select the transaction to pay.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              {transactionId ? (
                // Display transaction info if ID is provided
                transaction && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-medium text-gray-900">{transaction.transaction_number}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Customer: {transaction.shop_owner_name}
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Total Amount</p>
                        <p className="font-medium">ZMW {transaction.total_amount.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Balance</p>
                        <p className="font-medium text-red-600">ZMW {transaction.balance.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Select Transaction *
                  </label>
                  <select
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={formData.transaction_id}
                    onChange={(e) => setFormData({...formData, transaction_id: e.target.value})}
                  >
                    <option value="">Choose a transaction</option>
                    {transactions.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.transaction_number} - {t.shop_owner_name} (ZMW {t.balance.toFixed(2)})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Payment Details</h3>
              <p className="mt-1 text-sm text-gray-500">
                Enter payment information.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Payment Date *
                  </label>
                  <input
                    type="date"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                    max={format(new Date(), 'yyyy-MM-dd')}
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Payment Method *
                  </label>
                  <select
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                  >
                    <option value="cash">Cash</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="check">Check</option>
                  </select>
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Amount (ZMW) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedTransaction?.balance}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value)})}
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Reference Number
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={formData.reference_number}
                    onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                    placeholder="Transaction ID, Check No, etc."
                  />
                </div>

                <div className="col-span-6">
                  <label className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    rows="3"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/payments')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreatePayment;